const ROOT = '[data-wt-finder-root]';
const SIZE_IDS = new Set(['xs', 'small', 'medium', 'large', 'xl', 'xxl']);
const STYLE_IDS = new Set(['gentle', 'steady', 'power']);
const CUTS = new Set(['whole', 'split']);
const STRING_KEYS = ['availableTitle', 'availableCopy', 'selectedCopy', 'overrideFallbackCopy',
  'splitTitle', 'wholeTitle', 'splitCopy', 'wholeCopy', 'broaderCopy', 'missingCopy', 'errorCopy',
  'viewChew', 'browseAvailable', 'browseSplit', 'browseWhole', 'browseSize', 'emptyTitle', 'emptyCopy'];
const MAX_CONFIG_LENGTH = 524288;
const record = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const positiveInteger = value => Number.isSafeInteger(value) && value > 0;

function assertRecord(value, allowed) {
  if (!record(value) || Object.keys(value).some(key => !allowed.includes(key))) {
    throw new TypeError('Unexpected finder configuration shape.');
  }
}

function text(value, maximum = 2000) {
  if (typeof value !== 'string' || value.length > maximum || value.includes('\u0000')) {
    throw new TypeError('Invalid finder text.');
  }
  return value.trim();
}

function list(value, maximum) {
  if (!Array.isArray(value) || value.length > maximum) throw new TypeError('Invalid finder list.');
  return value;
}

// Accept only real storefront routes. A fixed parsing origin cannot be changed by <base> markup.
export function safeFinderPath(value, kind = 'browse') {
  if (typeof value !== 'string' || !value || value.length > 2048 || /[\s\u0000-\u001f\u007f-\u009f\\]/.test(value)
    || !value.startsWith('/') || value.startsWith('//') || value.includes('#')) return null;
  try {
    const url = new URL(value, 'https://finder.invalid');
    if (url.origin !== 'https://finder.invalid' || url.username || url.password) return null;
    const rawPath = value.split('?')[0];
    const parts = rawPath.split('/').slice(1);
    if (parts.length === 3) {
      if (!/^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8}){0,2}$/.test(parts.shift())) return null;
    }
    if (parts.length !== 2) return null;
    const resource = parts[0];
    if (!(kind === 'product' ? resource === 'products' : kind === 'collection' ? resource === 'collections'
      : kind === 'page' ? resource === 'pages' : ['products', 'collections'].includes(resource))) return null;
    const handle = decodeURIComponent(parts[1]);
    if (!handle || handle.length > 255 || !/^[\p{L}\p{N}][\p{L}\p{N}_-]*$/u.test(handle)) return null;
    if (url.search && (resource !== 'products' || !/^\?variant=[1-9]\d{0,15}$/.test(url.search)
      || !positiveInteger(Number(url.search.slice(9))))) return null;
    return url.pathname + url.search;
  } catch {
    return null;
  }
}

function product(value) {
  if (!record(value) || !positiveInteger(value.id) || value.available !== true || typeof value.title !== 'string'
    || !value.title.trim() || value.title.length > 500 || value.title.includes('\u0000')) return null;
  const url = safeFinderPath(value.url, 'product');
  return url ? { id: value.id, title: value.title.trim(), url, available: true } : null;
}

function distinctProducts(values, maximum) {
  const seen = new Set();
  return values.map(product).filter(item => item && !seen.has(item.id) && seen.add(item.id)).slice(0, maximum);
}

export function parseFinderConfig(input) {
  if (typeof input === 'string' && input.length > MAX_CONFIG_LENGTH) throw new TypeError('Finder configuration is too large.');
  const raw = typeof input === 'string' ? JSON.parse(input) : input;
  assertRecord(raw, ['version', 'mode', 'maxResults', 'showSizeLink', 'shopUrl', 'wholeUrl', 'splitUrl',
    'sizes', 'styles', 'quickPicks', 'rules', 'catalog', 'strings']);
  if (raw.version !== 2 || !['catalog', 'curated', 'collection'].includes(raw.mode)
    || !positiveInteger(raw.maxResults) || raw.maxResults > 4 || typeof raw.showSizeLink !== 'boolean') {
    throw new TypeError('Invalid finder settings.');
  }
  const sizeKeys = new Set();
  const sizes = list(raw.sizes, 6).map(item => {
    assertRecord(item, ['id', 'url', 'label']);
    if (!SIZE_IDS.has(item.id)) throw new TypeError('Unknown finder size.');
    return { id: item.id, url: safeFinderPath(item.url, 'collection'), label: item.label === undefined ? '' : text(item.label, 500) };
  }).filter(item => !sizeKeys.has(item.id) && sizeKeys.add(item.id));
  const styleKeys = new Set();
  const styles = list(raw.styles, 3).map(item => {
    assertRecord(item, ['id', 'cut', 'label']);
    if (!STYLE_IDS.has(item.id) || (!CUTS.has(item.cut) && item.cut !== 'either')) throw new TypeError('Unknown finder style.');
    return { id: item.id, cut: item.cut, label: item.label === undefined ? '' : text(item.label, 500) };
  }).filter(item => !styleKeys.has(item.id) && styleKeys.add(item.id));
  const quickKeys = new Set();
  const quickPicks = list(raw.quickPicks === undefined ? [] : raw.quickPicks, 18).map(item => {
    assertRecord(item, ['size', 'style', 'products']);
    if (!SIZE_IDS.has(item.size) || !STYLE_IDS.has(item.style)) throw new TypeError('Unknown finder quick pick.');
    return { size: item.size, style: item.style, products: distinctProducts(list(item.products, 1), 1) };
  }).filter(item => {
    const key = `${item.size}:${item.style}`;
    if (quickKeys.has(key)) return false;
    quickKeys.add(key); return true;
  });
  const ruleKeys = new Set();
  const rules = list(raw.rules, 48).map(item => {
    assertRecord(item, ['size', 'cut', 'style', 'products', 'note', 'collectionUrl']);
    if (!SIZE_IDS.has(item.size) || (!CUTS.has(item.cut) && item.cut !== 'either') || (item.style !== 'any' && !STYLE_IDS.has(item.style))) {
      throw new TypeError('Unknown finder rule.');
    }
    return { size: item.size, cut: item.cut, style: item.style,
      products: distinctProducts(list(item.products, 4), 4),
      note: item.note === undefined ? '' : text(item.note), collectionUrl: safeFinderPath(item.collectionUrl, 'collection') };
  }).filter(item => {
    // A specific-style block owns that size/style, even when its selected products sell out.
    const key = `${item.size}:${item.style}${item.style === 'any' ? `:${item.cut}` : ''}`;
    if (ruleKeys.has(key)) return false;
    ruleKeys.add(key); return true;
  });
  const catalog = list(raw.catalog, 300).map(item => {
    if (!record(item) || !SIZE_IDS.has(item.size) || !CUTS.has(item.cut)) throw new TypeError('Invalid finder catalog.');
    const candidate = product(item);
    return candidate ? { ...candidate, size: item.size, cut: item.cut } : null;
  }).filter(Boolean);
  assertRecord(raw.strings, STRING_KEYS);
  const strings = Object.fromEntries(STRING_KEYS.map(key => [key, text(raw.strings[key])]));
  return { version: 2, mode: raw.mode, maxResults: raw.maxResults, showSizeLink: raw.showSizeLink,
    shopUrl: safeFinderPath(raw.shopUrl, 'collection'), wholeUrl: safeFinderPath(raw.wholeUrl, 'collection'),
    splitUrl: safeFinderPath(raw.splitUrl, 'collection'), sizes, styles, quickPicks, rules, catalog, strings };
}

export function recommendFinder(config, sizeId, styleId) {
  const strings = config.strings;
  const size = config.sizes.find(item => item.id === sizeId);
  const style = config.styles.find(item => item.id === styleId);
  if (!size || !style) return { state: 'empty', title: strings.emptyTitle, copy: strings.emptyCopy, products: [], link: null, sizeLink: null };
  const quickPick = config.quickPicks?.find(item => item.size === sizeId && item.style === styleId);
  const exact = config.rules.find(item => item.size === sizeId && item.style === styleId);
  // A nonblank direct picker owns this combination, including an unavailable selected product.
  const rule = quickPick || exact || config.rules.find(item => item.size === sizeId && item.style === 'any'
    && (style.cut === 'either' || item.cut === 'either' || item.cut === style.cut));
  const collectionUrl = rule?.collectionUrl || (style.cut === 'whole' ? config.wholeUrl
    : style.cut === 'split' ? config.splitUrl : null) || size.url || config.shopUrl;
  let products = [];
  let manual = false;
  if (config.mode !== 'collection') {
    products = rule?.products.slice(0, config.maxResults) || [];
    manual = products.length > 0;
    if (!products.length && config.mode === 'catalog') {
      products = distinctProducts(config.catalog.filter(item => item.size === sizeId
        && (style.cut === 'either' || item.cut === style.cut)), config.maxResults);
    }
  }
  let title;
  let copy;
  let link;
  if (products.length) {
    title = products[0].title;
    copy = manual ? rule.note || strings.selectedCopy : strings.availableCopy;
    if (rule && !manual) copy = `${strings.overrideFallbackCopy} ${copy}`.trim();
    link = { url: products[0].url, label: strings.viewChew || products[0].title, kind: 'product' };
  } else if (config.mode === 'curated') {
    title = strings.emptyTitle; copy = strings.emptyCopy;
    if (rule?.note) copy = `${rule.note} ${copy}`.trim();
    if (quickPick) copy = `${strings.overrideFallbackCopy} ${copy}`.trim();
    link = collectionUrl ? { url: collectionUrl, label: strings.browseAvailable, kind: 'collection' } : null;
  } else {
    title = style.cut === 'whole' ? strings.wholeTitle : style.cut === 'split' ? strings.splitTitle : strings.availableTitle;
    copy = config.mode === 'collection' ? strings.missingCopy
      : `${strings.broaderCopy} ${style.cut === 'whole' ? strings.wholeCopy : style.cut === 'split' ? strings.splitCopy : strings.missingCopy}`.trim();
    if (rule && config.mode !== 'collection') copy = `${strings.overrideFallbackCopy} ${copy}`.trim();
    link = collectionUrl ? { url: collectionUrl, label: style.cut === 'whole' ? strings.browseWhole
      : style.cut === 'split' ? strings.browseSplit : strings.browseAvailable, kind: 'collection' } : null;
  }
  const sizeLink = config.showSizeLink && size.url && size.url !== link?.url
    ? { url: size.url, label: strings.browseSize, kind: 'collection' } : null;
  return { state: products.length ? 'products' : 'collection', title, copy, products, link, sizeLink };
}

function own(root, selector) {
  if (root.matches(selector)) return root;
  return [...root.querySelectorAll(selector)].find(node => node.closest(ROOT) === root) || null;
}

function nodesFor(root) {
  const nodes = Object.fromEntries(Object.entries({ data: '[data-wt-finder-config]', weight: '[data-wt-weight]',
    style: '[data-wt-style]', title: '[data-wt-result-title]', copy: '[data-wt-result-copy]', link: '[data-wt-result-link]',
    sizeLink: '[data-wt-result-size-link]', results: '[data-wt-finder-results]', guide: '[data-wt-finder-guide-link]',
    editorHelp: '[data-wt-finder-editor-help]' })
    .map(([key, selector]) => [key, own(root, selector)]));
  nodes.editorChoices = nodes.editorHelp ? [...nodes.editorHelp.querySelectorAll('[data-wt-finder-editor-choice]')]
    .filter(node => node.closest(ROOT) === root).slice(0, 18) : [];
  nodes.editorChoiceState = nodes.editorChoices.map(node => node.getAttribute('data-wt-finder-editor-choice')).join('\u0000');
  nodes.options = [nodes.weight, nodes.style].flatMap(node => node ? [...node.querySelectorAll('option')] : []);
  nodes.optionState = nodes.options.map(node => `${node.value}\u0000${node.textContent}\u0000${node.disabled}`).join('\u0001');
  return nodes;
}

function sameNodes(first, second) {
  return ['data', 'weight', 'style', 'title', 'copy', 'link', 'sizeLink', 'results', 'guide', 'editorHelp'].every(key => first[key] === second[key])
    && first.optionState === second.optionState && first.options.length === second.options.length
    && first.options.every((node, index) => node === second.options[index])
    && first.editorChoiceState === second.editorChoiceState && first.editorChoices.length === second.editorChoices.length
    && first.editorChoices.every((node, index) => node === second.editorChoices[index]);
}

function pageOrigin(page) {
  try {
    const origin = page.defaultView.location.origin;
    const parsed = new URL(origin);
    return ['https:', 'http:'].includes(parsed.protocol) && parsed.origin === origin ? origin : null;
  } catch { return null; }
}

function setLink(node, model, origin) {
  if (!node) return;
  const path = model && safeFinderPath(model.url, model.kind);
  const label = model && typeof model.label === 'string' ? model.label.trim() : '';
  if (!path || !label || !origin) {
    node.hidden = true; node.removeAttribute('href'); return;
  }
  node.textContent = label;
  node.href = new URL(path, origin).href;
  node.hidden = false;
}

function guidePath(value, origin) {
  const relative = safeFinderPath(value, 'page');
  if (relative) return relative;
  try {
    const url = new URL(value);
    return origin && url.origin === origin && !url.username && !url.password
      ? safeFinderPath(url.pathname + url.search + url.hash, 'page') : null;
  } catch { return null; }
}

const controllers = new Map();
const unloaded = new WeakSet();
let observer;
let suspended = false;
let queued = false;

function mount(root, page) {
  const nodes = nodesFor(root);
  const dataText = nodes.data?.textContent || '';
  const previous = controllers.get(root);
  if (previous && previous.dataText === dataText && sameNodes(previous.nodes, nodes)) return;
  previous?.destroy();
  const initialTitle = nodes.title?.textContent || '';
  const initialCopy = root.getAttribute('data-wt-finder-error-copy') || nodes.copy?.textContent || '';
  const origin = pageOrigin(page);
  let config;
  try { config = parseFinderConfig(dataText); }
  catch { console.warn('WhiteTail: invalid chew finder configuration; recommendation links are disabled.'); }
  if (nodes.weight) nodes.weight.disabled = !config || !config.sizes.length;
  if (nodes.style) nodes.style.disabled = !config || !config.styles.length;
  const clearResults = () => { if (nodes.results) while (nodes.results.firstChild) nodes.results.firstChild.remove(); };
  const updateEditorHelp = () => {
    if (!nodes.editorHelp) return;
    const valid = config && config.sizes.some(item => item.id === nodes.weight?.value)
      && config.styles.some(item => item.id === nodes.style?.value);
    const key = valid ? `${nodes.weight.value}:${nodes.style.value}` : null;
    let shown = false;
    for (const choice of nodes.editorChoices) {
      choice.hidden = !key || choice.getAttribute('data-wt-finder-editor-choice') !== key;
      shown = shown || !choice.hidden;
    }
    nodes.editorHelp.hidden = !shown;
  };
  const update = () => {
    if (!root.isConnected || suspended) return;
    clearResults();
    updateEditorHelp();
    if (!config || !nodes.weight || !nodes.style) {
      if (nodes.title) nodes.title.textContent = initialTitle;
      if (nodes.copy) nodes.copy.textContent = initialCopy;
      setLink(nodes.link, null, origin); setLink(nodes.sizeLink, null, origin);
      root.dataset.wtFinderState = 'error'; return;
    }
    const model = recommendFinder(config, nodes.weight.value, nodes.style.value);
    if (nodes.title) nodes.title.textContent = model.title;
    if (nodes.copy) nodes.copy.textContent = model.copy;
    setLink(nodes.link, model.link, origin); setLink(nodes.sizeLink, model.sizeLink, origin);
    if (nodes.results) {
      for (const item of model.products.slice(1)) {
        const entry = page.createElement('li'); entry.className = 'wt-finder-products__item';
        const link = page.createElement('a'); link.className = 'wt-home__text-link wt-finder-products__link';
        setLink(link, { url: item.url, label: item.title, kind: 'product' }, origin);
        if (!link.hidden) { entry.append(link); nodes.results.append(entry); }
      }
      nodes.results.hidden = !nodes.results.firstChild;
    }
    root.dataset.wtFinderState = model.state;
  };
  // Cancels implicit Enter submission too, without disabling normal keyboard use of selects or links.
  const preventSubmit = event => { event.preventDefault(); };
  root.addEventListener('submit', preventSubmit);
  nodes.weight?.addEventListener('change', update); nodes.style?.addEventListener('change', update);
  if (nodes.guide) {
    const path = guidePath(nodes.guide.getAttribute('data-wt-finder-guide-path') || nodes.guide.getAttribute('href'), origin);
    if (!path || !origin) { nodes.guide.hidden = true; nodes.guide.removeAttribute('href'); }
    else { nodes.guide.href = new URL(path, origin).href; nodes.guide.hidden = false; }
  }
  controllers.set(root, { nodes, dataText, destroy() {
    root.removeEventListener('submit', preventSubmit);
    nodes.weight?.removeEventListener('change', update); nodes.style?.removeEventListener('change', update);
    controllers.delete(root);
  } });
  update();
}

export function mountFinders(page = document) {
  if (suspended) return;
  for (const [root, controller] of [...controllers]) if (!root.isConnected) controller.destroy();
  for (const root of [...page.querySelectorAll(ROOT)].slice(0, 32)) if (!unloaded.has(root)) mount(root, page);
}

function start(page) {
  if (suspended) return;
  mountFinders(page);
  if (!observer && typeof MutationObserver !== 'undefined' && (page.body || page.documentElement)) {
    observer = new MutationObserver(() => {
      if (queued || suspended) return;
      queued = true;
      Promise.resolve().then(() => { queued = false; mountFinders(page); });
    });
    observer.observe(page.body || page.documentElement, { childList: true, subtree: true, characterData: true });
  }
}

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  const page = document;
  if (page.readyState === 'loading') page.addEventListener('DOMContentLoaded', () => start(page), { once: true });
  else start(page);
  page.addEventListener('shopify:section:load', event => {
    for (const root of page.querySelectorAll(ROOT)) if (event.target === root || event.target.contains(root)) unloaded.delete(root);
    start(page);
  });
  page.addEventListener('shopify:section:unload', event => {
    for (const [root, controller] of [...controllers]) if (event.target === root || event.target.contains(root)) {
      unloaded.add(root); controller.destroy();
    }
  });
  window.addEventListener('pagehide', () => {
    suspended = true; observer?.disconnect(); observer = undefined;
    for (const controller of [...controllers.values()]) controller.destroy();
  });
  window.addEventListener('pageshow', () => { suspended = false; start(page); });
}
